/**
 * 游标分页列表（spec 00 §3.1-6：一律游标制，禁止 offset 深翻页）。
 *
 * 游标制没有「第 N 页」的概念，所以要支持 Skill 要求的「上一页 / 下一页」，
 * 得自己维护一个游标栈：前进时把当前游标压栈，后退时弹栈。
 * 这样既不需要后端提供 prev_cursor，也不会退化成前端连翻抽干。
 *
 * 参数变化（换关键词、换状态筛选）时必须清空栈回到第一页，
 * 否则会拿旧游标去查新条件，翻出一页对不上筛选条件的数据。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery, type QueryKey } from '@tanstack/react-query';
import { PAGE_LIMIT, type CursorPage } from '@/api/client';

interface Options<T, P> {
  /** 参数里**不要**含 cursor，cursor 由本 hook 注入 */
  params: P;
  queryKey: (params: P & { cursor: string | null }) => QueryKey;
  fetcher: (
    params: P & { cursor?: string; limit: number },
    signal: AbortSignal,
  ) => Promise<CursorPage<T>>;
  limit?: number;
  enabled?: boolean;
}

export interface CursorListResult<T> {
  items: T[];
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  /** 第一页且零条 —— 用来区分「空库」和「空筛」，文案不一样（Skill pages.md） */
  isEmpty: boolean;
  hasPrev: boolean;
  hasNext: boolean;
  goPrev: () => void;
  goNext: () => void;
  refetch: () => void;
}

export function useCursorList<T, P extends object>(opts: Options<T, P>): CursorListResult<T> {
  const limit = opts.limit ?? PAGE_LIMIT;
  const [stack, setStack] = useState<(string | null)[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);

  // 参数指纹变了就回到第一页。用序列化比较而不是引用比较，
  // 因为调用方基本都是每次渲染新建一个 params 字面量。
  const fingerprint = JSON.stringify(opts.params);
  const prevFingerprint = useRef(fingerprint);
  useEffect(() => {
    if (prevFingerprint.current !== fingerprint) {
      prevFingerprint.current = fingerprint;
      setStack([]);
      setCursor(null);
    }
  }, [fingerprint]);

  const query = useQuery({
    queryKey: opts.queryKey({ ...opts.params, cursor }),
    queryFn: ({ signal }) =>
      opts.fetcher({ ...opts.params, ...(cursor ? { cursor } : {}), limit }, signal),
    enabled: opts.enabled ?? true,
    // 翻页时保留上一页内容，避免表格整块闪白
    placeholderData: keepPreviousData,
  });

  const goNext = useCallback(() => {
    const next = query.data?.next_cursor;
    if (!next) return;
    setStack((s) => [...s, cursor]);
    setCursor(next);
  }, [cursor, query.data?.next_cursor]);

  const goPrev = useCallback(() => {
    setStack((s) => {
      if (s.length === 0) return s;
      setCursor(s[s.length - 1] ?? null);
      return s.slice(0, -1);
    });
  }, []);

  const items = query.data?.items ?? [];

  return {
    items,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    isEmpty: !query.isLoading && items.length === 0 && stack.length === 0,
    hasPrev: stack.length > 0,
    hasNext: query.data?.has_more ?? false,
    goPrev,
    goNext,
    refetch: () => void query.refetch(),
  };
}
